<template>
  <div class="asset-category-page">
    <div class="toolbar">
      <Button color="primary" @click="openCreate">新增类别</Button>
      <Button @click="load">刷新</Button>
      <Button @click="initDefault" v-if="list.length === 0">初始化默认类别</Button>
    </div>

    <Table :datas="list" :loading="loading">
      <TableItem title="类别编码" prop="code" align="center" :width="100"></TableItem>
      <TableItem title="类别名称" prop="name" align="left"></TableItem>
      <TableItem title="默认使用年限(月)" prop="defaultUsefulLife" align="center" :width="140"></TableItem>
      <TableItem title="默认净残值率(%)" align="center" :width="130">
        <template slot-scope="{data}">
          {{ data.defaultResidualRate }}%
        </template>
      </TableItem>
      <TableItem title="默认折旧方法" align="center" :width="120">
        <template slot-scope="{data}">
          {{ getDepreciationMethodText(data.defaultDepreciationMethod) }}
        </template>
      </TableItem>
      <TableItem title="排序" prop="sortOrder" align="center" :width="80"></TableItem>
      <TableItem title="操作" align="center" :width="150">
        <template slot-scope="{data}">
          <Button text size="xs" @click="openEdit(data)">编辑</Button>
          <Button text size="xs" color="red" @click="remove(data)">删除</Button>
        </template>
      </TableItem>
    </Table>

    <!-- 新增/编辑弹窗 -->
    <Modal v-model="showModal" :hasCloseIcon="true" :width="760" class="asset-category-modal">
      <div class="modal-header" :class="isEdit ? 'modal-header-edit' : 'modal-header-create'">
        <div class="modal-title">{{ isEdit ? '编辑资产类别' : '新增资产类别' }}</div>
        <div class="modal-subtitle">{{ isEdit ? '修改类别默认折旧参数' : '添加新的资产类别和默认折旧参数' }}</div>
      </div>
      <Form ref="form" :model="form" :rules="rules" labelPosition="right" :labelWidth="130" class="category-form">
        <div class="form-row">
          <FormItem label="类别编码" prop="code" class="form-col">
            <input v-model="form.code" placeholder="留空自动生成" />
          </FormItem>
          <FormItem label="类别名称" prop="name" class="form-col">
            <input v-model="form.name" placeholder="请输入类别名称" />
          </FormItem>
        </div>
        <div class="form-row">
          <FormItem label="使用年限（月）" class="form-col">
            <input v-model.number="form.defaultUsefulLife" type="number" placeholder="如：60（5年）" />
          </FormItem>
          <FormItem label="净残值率（%）" class="form-col">
            <input v-model.number="form.defaultResidualRate" type="number" placeholder="如：5" />
          </FormItem>
        </div>
        <div class="form-row">
          <FormItem label="折旧方法" class="form-col">
            <Select v-model="form.defaultDepreciationMethod" :datas="depreciationMethods" placeholder="请选择折旧方法" />
          </FormItem>
          <FormItem label="排序" class="form-col">
            <input v-model.number="form.sortOrder" type="number" placeholder="数字越小越靠前" />
          </FormItem>
        </div>
        <FormItem label="备注" class="form-full">
          <input v-model="form.remark" placeholder="请输入备注" />
        </FormItem>
      </Form>
      <div slot="footer">
        <Button color="primary" @click="save" size="l">保存</Button>
        <Button @click="showModal = false" size="l">取消</Button>
      </div>
    </Modal>
  </div>
</template>

<script>
import api from '../../api/assetcategory';

export default {
  name: 'AssetCategory',
  data() {
    return {
      list: [],
      loading: false,
      showModal: false,
      isEdit: false,
      form: {
        code: '',
        name: '',
        defaultUsefulLife: 60,
        defaultResidualRate: 5,
        defaultDepreciationMethod: 0,
        sortOrder: 0,
        remark: ''
      },
      depreciationMethods: [
        { title: '平均年限法', key: 0 },
        { title: '工作量法', key: 1 },
        { title: '双倍余额递减法', key: 2 },
        { title: '年数总和法', key: 3 }
      ],
      rules: {
        name: [{ required: true, message: '请输入类别名称' }]
      }
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const res = await api.getAssetCategories();
        this.list = res.data || [];
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.isEdit = false;
      this.form = {
        code: '',
        name: '',
        defaultUsefulLife: 60,
        defaultResidualRate: 5,
        defaultDepreciationMethod: 0,
        sortOrder: 0,
        remark: ''
      };
      this.showModal = true;
    },
    openEdit(data) {
      this.isEdit = true;
      this.form = { ...data };
      this.showModal = true;
    },
    async save() {
      const validResult = await this.$refs.form.valid();
      const valid = typeof validResult === 'boolean' ? validResult : validResult && validResult.result;
      if (!valid) {
        this.$Message.error('请完善资产类别必填信息');
        return;
      }

      try {
        if (this.isEdit) {
          await api.updateAssetCategory(this.form.id, this.form);
        } else {
          await api.createAssetCategory(this.form);
        }
        this.showModal = false;
        this.$Message.success('保存成功');
        this.load();
      } catch (e) {
        this.$Message.error(e.msg || '保存失败');
      }
    },
    async remove(data) {
      if (!confirm(`确认删除类别 ${data.name}？此操作不可恢复！`)) return;
      try {
        await api.deleteAssetCategory(data.id);
        this.$Message.success('删除成功');
        this.load();
      } catch (e) {
        this.$Message.error(e.msg || '删除失败');
      }
    },
    getDepreciationMethodText(method) {
      const map = { 0: '平均年限法', 1: '工作量法', 2: '双倍余额递减法', 3: '年数总和法' };
      return map[method] || '未知';
    },
    async initDefault() {
      if (!confirm('确认初始化默认资产类别？')) return;
      try {
        await api.initDefaultAssetCategories();
        this.$Message.success('初始化成功');
        this.load();
      } catch (e) {
        this.$Message.error(e.msg || '初始化失败');
      }
    }
  }
};
</script>

<style scoped>
.asset-category-page {
  padding: 20px;
}
.toolbar {
  margin-bottom: 16px;
}
.toolbar button {
  margin-right: 8px;
}

/* 弹窗头部标题 */
.modal-header {
  margin: -20px -20px 10px -20px;
  padding: 12px 20px;
  border-radius: 4px 4px 0 0;
}
.modal-header-create {
  background: linear-gradient(135deg, #1890ff 0%, #36cfc9 100%);
}
.modal-header-edit {
  background: linear-gradient(135deg, #722ed1 0%, #531dab 100%);
}
.modal-title {
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  line-height: 1.3;
}
.modal-subtitle {
  font-size: 12px;
  color: rgba(255,255,255,0.85);
  margin-top: 1px;
}

/* 弹窗样式优化 */
:deep(.asset-category-modal .h-modal) {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
}
:deep(.asset-category-modal .h-modal-body) {
  max-height: none;
  overflow-y: visible;
  padding: 0 20px 0 20px;
}
:deep(.asset-category-modal .h-modal-footer) {
  border-top: 1px solid #f0f0f0;
  padding: 8px 20px;
  background: #fafafa;
  display: flex;
  justify-content: center;
  gap: 12px;
}
:deep(.asset-category-modal .h-modal-footer .h-btn) {
  padding: 0 24px;
  height: 34px;
  font-size: 14px;
  border-radius: 6px;
  min-width: 96px;
}
.category-form {
  padding-bottom: 6px;
}
.form-row {
  display: flex;
  gap: 14px;
  margin-bottom: 2px;
  align-items: flex-start;
}
.form-col {
  flex: 1;
  margin-bottom: 0;
}
.form-full {
  margin-bottom: 0;
}
.form-col :deep(.h-form-item),
.form-full:deep(.h-form-item) {
  margin-bottom: 0 !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  display: flex;
  align-items: flex-start;
  flex-wrap: nowrap;
  min-height: 40px;
}
.form-col :deep(.h-form-item-content),
.form-full:deep(.h-form-item-content) {
  flex: 1;
  min-width: 0;
}
.form-col :deep(.h-form-item-wrap),
.form-full:deep(.h-form-item-wrap) {
  min-height: 34px;
  display: flex;
  align-items: center;
}
.form-col :deep(.h-form-item-error),
.form-full:deep(.h-form-item-error) {
  margin-top: 1px;
  line-height: 14px;
  color: #f5222d;
  font-size: 12px;
}
.form-col :deep(.h-form-item-error-label),
.form-full:deep(.h-form-item-error-label) {
  display: none;
}
.form-col :deep(.h-form-item-label),
.form-full:deep(.h-form-item-label) {
  width: 118px;
  height: 34px;
  padding-right: 8px;
  font-size: 14px;
  font-weight: normal;
  color: #595959;
  line-height: 34px;
  text-align: right;
  white-space: nowrap;
  flex-shrink: 0;
}
.form-col :deep(.h-form-item-required .h-form-item-label::before),
.form-full:deep(.h-form-item-required .h-form-item-label::before) {
  content: '* ';
  color: #ff4d4f;
  margin-right: 2px;
}
.form-col input,
.form-full input,
.form-col :deep(.h-select) {
  width: 100%;
  height: 34px;
  font-size: 14px;
  flex: 1;
  box-sizing: border-box;
}
.form-col input,
.form-full input {
  border-radius: 6px;
  border: 1px solid #d9d9d9;
  padding: 0 10px;
  transition: all 0.2s ease;
  background: #fafafa;
  color: #333;
}
.form-col input:hover,
.form-full input:hover {
  border-color: #40a9ff;
  background: #fff;
}
.form-col input:focus,
.form-full input:focus {
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
  background: #fff;
  outline: none;
}
.form-col :deep(.h-select .h-select-show) {
  height: 34px !important;
  border-radius: 6px;
  border: 1px solid #d9d9d9;
  transition: all 0.2s ease;
  background: #fafafa;
  box-sizing: border-box;
}
.form-col :deep(.h-select .h-select-show:hover) {
  border-color: #40a9ff;
  background: #fff;
}
.form-col :deep(.h-select .h-select-show.h-select-focus) {
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
  background: #fff;
}
.form-col :deep(.h-form-item-valid-error) input,
.form-full:deep(.h-form-item-valid-error) input,
.form-col :deep(.h-form-item-valid-error .h-select-show) {
  border-color: #f5222d;
  background: #fffafa;
}
</style>
